/* Phase 3 — WHO Guides™ workspace. Case queue + coordination detail.
   Guides see only their own cases. Every action is a server-validated
   state transition with audit trail. */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Plus, X, ShieldAlert, Clock, AlertTriangle, CheckCircle2,
  ArrowRight, User, Building2, FileCheck2, RefreshCcw, Lock, StickyNote,
} from "lucide-react";
import Seo from "@/components/Seo";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import { useUserAuth } from "@/context/UserAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE = {
  need_identified: ["bg-navy/5 text-navy border-navy/15", "Need identified"],
  viability_assessment: ["bg-navy/5 text-navy border-navy/15", "Viability assessment"],
  requesting_permission: ["bg-gold/15 text-gold-deep border-gold/40", "Requesting permission"],
  warm_handoff: ["bg-gold/15 text-gold-deep border-gold/40", "Warm handoff"],
  provider_response: ["bg-sky-50 text-sky-800 border-sky-200", "Awaiting provider"],
  confirming_connection: ["bg-sky-50 text-sky-800 border-sky-200", "Confirming connection"],
  service_outcome: ["bg-green-50 text-green-800 border-green-200", "Service / outcome"],
  closing_coordination: ["bg-green-50 text-green-800 border-green-200", "Closing"],
  bounded_recovery: ["bg-amber-50 text-amber-800 border-amber-200", "Bounded recovery"],
  changing_pathway: ["bg-purple-50 text-purple-800 border-purple-200", "Changing pathway"],
  closed: ["bg-navy/5 text-navy/50 border-navy/15", "Closed"],
};

const chip = "inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-bold border";
const input = "w-full px-3 py-2.5 bg-white border border-borderSubtle rounded-md text-navy focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 text-sm";
const label = "block text-xs uppercase tracking-widest text-navy/60 font-semibold mb-1.5";
const btnGold = "inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-4 py-2.5 rounded-md text-sm disabled:opacity-50 transition-colors";
const btnGhost = "inline-flex items-center justify-center gap-2 border border-navy/25 hover:border-navy text-navy font-semibold px-3 py-2 rounded-md text-xs transition-colors";
const btnWarn = "inline-flex items-center justify-center gap-2 border border-amber-300 hover:bg-amber-50 text-amber-800 font-semibold px-3 py-2 rounded-md text-xs transition-colors";

export function StateChip({ value }) {
  const [cls, text] = STATE[value] || STATE.need_identified;
  return <span className={`${chip} ${cls}`} data-testid={`state-${value}`}>{text}</span>;
}

export function isOverdue(c) {
  return c.state !== "closed" && c.next_action_due_at && new Date(c.next_action_due_at) < new Date();
}

export default function GuideWorkspace() {
  const { user, loading, authHeaders } = useUserAuth();
  const [cases, setCases] = useState(null);
  const [selected, setSelected] = useState(null); // bundle from /cases/{id}
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  const isGuide = user && (user.roles.includes("who_guide") || user.roles.includes("administrator"));

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/coordination/cases`, { headers: authHeaders() });
    setCases(r.data.cases || []);
  }, [authHeaders]);

  const openCase = useCallback(async (id) => {
    const r = await axios.get(`${API}/coordination/cases/${id}`, { headers: authHeaders() });
    setSelected(r.data);
  }, [authHeaders]);

  useEffect(() => { if (isGuide) load().catch((e) => setErr(e?.response?.data?.detail || "Could not load cases.")); }, [isGuide, load]);

  const act = async (path, body = {}) => {
    setErr("");
    try {
      await axios.post(`${API}/coordination/cases/${selected.case.id}${path}`, body, { headers: authHeaders() });
      await openCase(selected.case.id);
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Action failed.");
    }
  };

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold-deep" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: "/guides" }} replace />;
  if (!isGuide) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-borderSubtle rounded-xl p-8 text-center" data-testid="guides-forbidden">
          <ShieldAlert className="w-10 h-10 text-red-600 mx-auto" />
          <h1 className="mt-4 font-display font-black text-navy text-2xl">WHO Guides™ workspace</h1>
          <p className="mt-2 text-slateSoft text-sm">This area requires the WHO Guide role.</p>
          <Link to="/" className={`${btnGhost} mt-6 inline-flex`}>Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="guides-workspace">
      <Seo title="WHO Guides™ Workspace" path="/guides" description="Coordination case workspace" />
      <PilotDemoBanner text="Pilot workspace — all cases shown here are synthetic demonstration records." />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-gold-deep">WHO Guides™</p>
            <h1 className="mt-2 font-display font-black text-navy text-3xl">Coordination workspace</h1>
            <p className="mt-1 text-sm text-slateSoft">A referral is not a result. Own every case until the connection is confirmed.</p>
          </div>
          <button className={btnGold} onClick={() => setCreating(true)} data-testid="case-create-btn"><Plus className="w-4 h-4" /> New case</button>
        </div>
        {err && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="guides-error">{err}</p>}

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          {/* Queue */}
          <div className="lg:col-span-5 space-y-3" data-testid="case-queue">
            {!cases && <Loader2 className="w-5 h-5 animate-spin text-gold-deep" />}
            {cases?.length === 0 && <p className="text-sm text-slateSoft bg-white border border-borderSubtle rounded-lg p-6 text-center">No cases yet. Open one to begin coordination.</p>}
            {cases?.map((c) => (
              <button
                key={c.id}
                onClick={() => openCase(c.id)}
                className={`w-full text-left bg-white border rounded-lg p-4 transition-colors hover:border-gold/60 ${selected?.case?.id === c.id ? "border-gold shadow-md" : "border-borderSubtle"}`}
                data-testid={`case-card-${c.id}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <StateChip value={c.state} />
                  <span className="flex items-center gap-1.5">
                    {c.needs_human_review && <span className={`${chip} bg-red-50 text-red-800 border-red-200`}><AlertTriangle className="w-3 h-3" /> Human review</span>}
                    {isOverdue(c) && <span className={`${chip} bg-amber-50 text-amber-800 border-amber-200`}><Clock className="w-3 h-3" /> Overdue</span>}
                  </span>
                </div>
                <p className="mt-2 font-semibold text-navy text-sm">{c.need_summary}</p>
                <p className="mt-1 text-xs text-slateSoft flex items-center gap-1.5 flex-wrap">
                  <User className="w-3 h-3" /> {c.veteran?.display_name || "Veteran"}
                  {c.provider_name && <><span className="text-navy/30">·</span><Building2 className="w-3 h-3" /> {c.provider_name}</>}
                </p>
                <p className="mt-1 text-xs text-navy/70">Next: {c.next_action}{c.next_action_due_at ? ` · due ${new Date(c.next_action_due_at).toLocaleString()}` : ""}</p>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {!selected && (
              <div className="bg-white border border-dashed border-borderSubtle rounded-lg p-12 text-center text-slateSoft text-sm" data-testid="case-detail-empty">
                Select a case to see its timeline, evidence, and next actions.
              </div>
            )}
            {selected && <CaseDetail bundle={selected} act={act} reload={() => { openCase(selected.case.id); load(); }} authHeaders={authHeaders} />}
          </div>
        </div>
      </div>
      {creating && <CreateCaseDialog authHeaders={authHeaders} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); load(); openCase(id); }} />}
    </div>
  );
}

function CaseDetail({ bundle, act, reload, authHeaders }) {
  const { case: c, events, responses, evidence, provider, consent, veteran } = bundle;
  const [providers, setProviders] = useState([]);
  const [picking, setPicking] = useState(false);
  const [closing, setClosing] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (picking && providers.length === 0) {
      axios.get(`${API}/discovery/providers`, { params: { limit: 100 } })
        .then((r) => setProviders((r.data.providers || []).filter((p) => p.organization_role !== "legislative_office" && p.coordinated_services_eligible !== false)));
    }
  }, [picking, providers.length]);

  return (
    <div className="bg-white border border-borderSubtle rounded-lg p-6 space-y-6" data-testid="case-detail">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <StateChip value={c.state} />
          {c.needs_human_review && <span className={`${chip} bg-red-50 text-red-800 border-red-200`}><AlertTriangle className="w-3 h-3" /> Needs human review</span>}
          <span className="text-xs text-slateSoft">recovery {c.recovery_attempts}/{c.max_recovery_attempts}</span>
        </div>
        <h2 className="mt-2 font-display font-black text-navy text-xl">{c.need_summary}</h2>
        <p className="mt-1 text-xs uppercase tracking-widest text-navy/50">{c.need_category.replace(/_/g, " ")}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div className="bg-cream-panel rounded-md p-3"><p className="text-[10px] uppercase tracking-widest text-navy/50 font-bold">Veteran</p><p className="mt-1 text-navy font-semibold">{veteran?.display_name || "—"}</p></div>
        <div className="bg-cream-panel rounded-md p-3"><p className="text-[10px] uppercase tracking-widest text-navy/50 font-bold">Provider</p><p className="mt-1 text-navy font-semibold">{provider?.display_name || "Not selected"}</p>{provider?.capacity_status && <p className="text-xs text-slateSoft capitalize">capacity: {provider.capacity_status}</p>}</div>
        <div className="bg-cream-panel rounded-md p-3"><p className="text-[10px] uppercase tracking-widest text-navy/50 font-bold">Consent</p><p className={`mt-1 font-semibold ${consent?.granted ? "text-green-700" : "text-amber-700"}`} data-testid="consent-status">{consent?.granted ? "Granted (scoped)" : "Not granted"}</p></div>
      </div>

      <div className="bg-navy/5 border border-navy/10 rounded-md p-3 text-sm">
        <p className="text-navy"><span className="font-semibold">Owner:</span> {c.owner_role} · <span className="font-semibold">Next:</span> {c.next_action}{c.next_action_due_at ? ` · due ${new Date(c.next_action_due_at).toLocaleString()}` : ""}</p>
      </div>

      {/* Actions */}
      {c.state !== "closed" && (
        <div className="border-t border-borderSubtle pt-4" data-testid="case-actions">
          <p className="eyebrow text-gold-deep mb-3">Guide actions</p>
          <div className="flex flex-wrap gap-2">
            {c.state === "need_identified" && <button className={btnGold} onClick={() => act("/begin-assessment")} data-testid="act-assess">Begin viability assessment <ArrowRight className="w-4 h-4" /></button>}
            {(c.state === "viability_assessment" || c.state === "changing_pathway") && (
              <button className={btnGold} onClick={() => setPicking(true)} data-testid="act-select-provider">Select provider <ArrowRight className="w-4 h-4" /></button>
            )}
            {c.state === "changing_pathway" && <button className={btnGhost} onClick={() => act("/back-to-assessment")} data-testid="act-back-assess">Reassess providers</button>}
            {c.state === "requesting_permission" && (
              consent?.granted
                ? <button className={btnGold} onClick={() => act("/handoff", { note: note || undefined })} data-testid="act-handoff">Send warm handoff <ArrowRight className="w-4 h-4" /></button>
                : <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="awaiting-permission">Awaiting the veteran's permission decision. Sharing is blocked until consent is granted.</p>
            )}
            {["provider_response", "confirming_connection", "bounded_recovery"].includes(c.state) && c.recovery_attempts < c.max_recovery_attempts && (
              <button className={btnWarn} onClick={() => act("/nudge")} data-testid="act-nudge"><RefreshCcw className="w-3.5 h-3.5" /> Follow up (recovery {c.recovery_attempts + 1}/{c.max_recovery_attempts})</button>
            )}
            {["provider_response", "confirming_connection", "bounded_recovery"].includes(c.state) && (
              <button className={btnGhost} onClick={() => act("/change-pathway")} data-testid="act-change-pathway">Change pathway</button>
            )}
            {c.state === "service_outcome" && <button className={btnGold} onClick={() => act("/begin-closing")} data-testid="act-begin-closing">Begin closing <ArrowRight className="w-4 h-4" /></button>}
            <button className={btnGhost} onClick={() => setClosing(true)} data-testid="act-close">Close case…</button>
          </div>
        </div>
      )}

      {/* Provider responses */}
      {responses.length > 0 && (
        <div data-testid="case-responses">
          <p className="eyebrow text-gold-deep mb-2">Provider responses</p>
          {responses.map((r) => (
            <div key={r.id} className="text-sm bg-cream-panel rounded-md p-3 mb-2">
              <span className="font-semibold text-navy capitalize">{r.response.replace(/_/g, " ")}</span>
              {r.decline_reason && <span className="text-slateSoft"> — {r.decline_reason.replace(/_/g, " ")}</span>}
              {r.detail && <p className="text-slateSoft mt-1">{r.detail}</p>}
              <p className="text-xs text-slateSoft mt-1">{new Date(r.responded_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Evidence */}
      <div data-testid="case-evidence">
        <p className="eyebrow text-gold-deep mb-2">Connection evidence</p>
        {evidence.length === 0 && <p className="text-xs text-slateSoft">No evidence submitted yet. Acceptance of a referral is never treated as a connection.</p>}
        {evidence.map((e) => (
          <div key={e.id} className="text-sm border border-borderSubtle rounded-md p-3 mb-2" data-testid={`evidence-${e.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`${chip} ${e.claim === "connected" || e.claim === "service_received" ? "bg-green-50 text-green-800 border-green-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{e.claim.replace(/_/g, " ")}</span>
              <span className={`${chip} bg-navy/5 text-navy border-navy/15`}>{e.source.replace(/_/g, " ")}</span>
              <span className={`${chip} ${e.status === "accepted" ? "bg-green-50 text-green-800 border-green-200" : e.status === "rejected" ? "bg-red-50 text-red-800 border-red-200" : "bg-navy/5 text-navy/60 border-navy/15"}`}>{e.status}</span>
              {e.is_late && <span className={`${chip} bg-amber-50 text-amber-800 border-amber-200`}>late</span>}
            </div>
            {e.statement && <p className="text-slateSoft mt-1.5">{e.statement}</p>}
            <p className="text-xs text-slateSoft mt-1">{new Date(e.submitted_at).toLocaleString()}</p>
            {e.status === "submitted" && e.source !== "veteran_self_report" && c.state !== "closed" && (
              <div className="mt-2 flex gap-2">
                <button className={btnGold} onClick={() => act(`/evidence/${e.id}/review`, { decision: "accept" })} data-testid={`evidence-accept-${e.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Accept</button>
                <button className={btnWarn} onClick={() => act(`/evidence/${e.id}/review`, { decision: "reject", review_note: "Reviewed — not sufficient" })} data-testid={`evidence-reject-${e.id}`}>Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Private case notes — guide/admin only, never shared */}
      <CaseNotes caseId={c.id} authHeaders={authHeaders} />

      {/* Timeline */}
      <div data-testid="case-timeline">
        <p className="eyebrow text-gold-deep mb-2">Timeline</p>
        <ol className="relative border-l border-borderSubtle ml-2 space-y-3">
          {events.map((e) => (
            <li key={e.id} className="ml-4 text-xs">
              <span className="absolute -left-1.5 mt-1 w-3 h-3 rounded-full bg-gold border-2 border-white" />
              <span className="font-semibold text-navy">{new Date(e.event_at).toLocaleString()}</span>
              <span className="text-slateSoft"> · {(e.from_state || "—").replace(/_/g, " ")} → {e.to_state.replace(/_/g, " ")} · {e.actor_role || "system"}</span>
              {e.note && <p className="text-slateSoft italic">{e.note}</p>}
            </li>
          ))}
        </ol>
      </div>

      {picking && (
        <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-center justify-center p-6" data-testid="provider-picker">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-start justify-between">
              <h3 className="font-display font-black text-navy text-lg">Select provider</h3>
              <button onClick={() => setPicking(false)} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-slateSoft">Selecting a provider requests the veteran's permission before any information is shared. Legislative offices are not eligible.</p>
            <div className="max-h-72 overflow-y-auto space-y-1">
              {providers.map((p) => (
                <button key={p.id} className="w-full text-left px-3 py-2.5 rounded-md border border-borderSubtle hover:border-gold text-sm text-navy" onClick={() => { act("/select-provider", { provider_id: p.id }); setPicking(false); }} data-testid={`pick-${p.id}`}>
                  {p.display_name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {closing && (
        <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-center justify-center p-6" data-testid="close-dialog">
          <form className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4" onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.target);
            setClosing(false);
            if (c.state !== "closing_coordination") await act("/begin-closing");
            await act("/close", { closure_outcome: fd.get("outcome"), closure_reason: fd.get("reason") });
            reload();
          }}>
            <div className="flex items-start justify-between">
              <h3 className="font-display font-black text-navy text-lg">Close coordination</h3>
              <button type="button" onClick={() => setClosing(false)} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-slateSoft">Cases never close silently. An outcome and reason are recorded permanently in the audit trail.</p>
            <label className="block"><span className={label}>Outcome</span>
              <select name="outcome" className={input} required data-testid="close-outcome">
                <option value="connected">Connected — connection confirmed</option>
                <option value="resolved_otherwise">Resolved another way</option>
                <option value="recovery_exhausted">Recovery exhausted (human review)</option>
                <option value="withdrawn_by_veteran">Withdrawn by veteran</option>
                <option value="administrative">Administrative</option>
              </select>
            </label>
            <label className="block"><span className={label}>Reason (required)</span>
              <textarea name="reason" className={`${input} min-h-[70px]`} required minLength={5} data-testid="close-reason" />
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setClosing(false)} className={btnGhost}>Cancel</button>
              <button type="submit" className={btnGold} data-testid="close-submit"><FileCheck2 className="w-4 h-4" /> Close case</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function CaseNotes({ caseId, authHeaders }) {
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/coordination/cases/${caseId}/notes`, { headers: authHeaders() });
    setNotes(r.data.notes || []);
  }, [caseId, authHeaders]);

  useEffect(() => { load().catch(() => setNotes([])); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setErr(""); setBusy(true);
    try {
      await axios.post(`${API}/coordination/cases/${caseId}/notes`, { body: draft.trim() }, { headers: authHeaders() });
      setDraft("");
      await load();
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : detail?.[0]?.msg || "Could not save note.");
    } finally { setBusy(false); }
  };

  const addAddendum = async (noteId, body) => {
    await axios.post(`${API}/coordination/notes/${noteId}/addendum`, { body }, { headers: authHeaders() });
    await load();
  };

  return (
    <div className="border-t border-borderSubtle pt-4" data-testid="case-notes">
      <p className="eyebrow text-gold-deep mb-1 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Private case notes</p>
      <p className="text-[11px] text-slateSoft mb-3">Visible only to you and WHO administrators. Never shared with the veteran or provider, never sent by SMS. Notes cannot be edited — corrections are added as timestamped addenda. Do not record SSNs or similar identifiers.</p>
      {err && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-2" data-testid="note-error">{err}</p>}
      {!notes && <Loader2 className="w-4 h-4 animate-spin text-gold-deep" />}
      <div className="space-y-2" data-testid="note-list">
        {notes?.map((n) => (
          <NoteCard key={n.id} note={n} onAddendum={addAddendum} />
        ))}
        {notes?.length === 0 && <p className="text-xs text-slateSoft">No notes yet.</p>}
      </div>
      <form onSubmit={submit} className="mt-3 space-y-2">
        <textarea
          className={`${input} min-h-[60px]`}
          maxLength={2000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a private note (max 2,000 characters)…"
          data-testid="note-input"
        />
        <button type="submit" className={btnGold} disabled={busy || draft.trim().length < 2} data-testid="note-submit">
          <StickyNote className="w-3.5 h-3.5" /> Save note
        </button>
      </form>
    </div>
  );
}

function NoteCard({ note, onAddendum }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await onAddendum(note.id, draft.trim());
      setAdding(false); setDraft("");
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : detail?.[0]?.msg || "Could not save addendum.");
    }
  };

  return (
    <div className="text-sm border border-borderSubtle rounded-md p-3 bg-cream-panel/50" data-testid={`note-${note.id}`}>
      <p className="text-navy whitespace-pre-wrap">{note.body}</p>
      <p className="text-xs text-slateSoft mt-1.5">
        {(note.author_role || "guide").replace(/_/g, " ")} · {new Date(note.created_at).toLocaleString()}
      </p>
      {note.addenda?.length > 0 && (
        <div className="mt-2 space-y-1.5 border-l-2 border-gold/50 pl-3">
          {note.addenda.map((a) => (
            <div key={a.id} data-testid={`addendum-${a.id}`}>
              <p className="text-navy/90 text-[13px] whitespace-pre-wrap">{a.body}</p>
              <p className="text-[11px] text-slateSoft">addendum · {(a.author_role || "").replace(/_/g, " ")} · {new Date(a.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
      {err && <p className="text-xs text-red-700 mt-2">{err}</p>}
      {adding ? (
        <form onSubmit={submit} className="mt-2 space-y-2">
          <textarea className={`${input} min-h-[50px] text-xs`} maxLength={2000} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Addendum / correction…" data-testid={`addendum-input-${note.id}`} />
          <div className="flex gap-2">
            <button type="submit" className={btnGold} disabled={draft.trim().length < 2} data-testid={`addendum-submit-${note.id}`}>Save addendum</button>
            <button type="button" className={btnGhost} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      ) : (
        <button className={`${btnGhost} mt-2`} onClick={() => setAdding(true)} data-testid={`addendum-open-${note.id}`}>Add addendum</button>
      )}
    </div>
  );
}

function CreateCaseDialog({ authHeaders, onClose, onCreated }) {
  const [veterans, setVeterans] = useState([]);
  const [form, setForm] = useState({ veteran_user_id: "", need_category: "employment", need_summary: "" });
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/coordination/veterans`, { headers: authHeaders() }).then((r) => setVeterans(r.data.veterans || []));
  }, [authHeaders]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const r = await axios.post(`${API}/coordination/cases`, form, { headers: authHeaders() });
      onCreated(r.data.case.id);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not create case.");
    }
  };

  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-center justify-center p-6" data-testid="case-create-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="font-display font-black text-navy text-lg">Open a coordination case</h3>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
        <label className="block"><span className={label}>Veteran</span>
          <select className={input} required value={form.veteran_user_id} onChange={(e) => setForm({ ...form, veteran_user_id: e.target.value })} data-testid="case-veteran">
            <option value="">— Select veteran —</option>
            {veterans.map((v) => <option key={v.id} value={v.id}>{v.display_name || v.email}</option>)}
          </select>
        </label>
        <label className="block"><span className={label}>Need category</span>
          <select className={input} value={form.need_category} onChange={(e) => setForm({ ...form, need_category: e.target.value })} data-testid="case-category">
            {["employment", "housing", "healthcare", "behavioral_health", "benefits", "legal", "financial", "transportation", "family_support", "peer_support", "education", "food_assistance", "resource_navigation", "community_engagement"].map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </label>
        <label className="block"><span className={label}>Need summary</span>
          <textarea className={`${input} min-h-[70px]`} required minLength={5} maxLength={500} value={form.need_summary} onChange={(e) => setForm({ ...form, need_summary: e.target.value })} placeholder="What does this person need, in one sentence?" data-testid="case-summary" />
        </label>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="submit" className={btnGold} data-testid="case-create-submit">Open case</button>
        </div>
      </form>
    </div>
  );
}
